import { create as string } from 'yup/lib/string';
import { create as mixed } from 'yup/lib/mixed';
import { create as object } from 'yup/lib/object';
import * as isValid from 'date-fns/is_valid';

export interface IValidators {
    [key: string]: Function;
}
export interface IValidationMessageObject {
    [key: string]: string;
}
export interface IValidatorObj {
    [key: string]: IValidationMessageObject;
}

const compose = (validators: IValidator[]) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let tests: any = [];
    validators.map(validator => (tests = tests.concat(validator.tests)));

    return Object.assign(validators[0], { tests });
};

export class FormValidator {
    public validators: IValidators;
    constructor() {
        this.validators = {};
        this.registerValidation('number', this.validateNumber);
        this.registerValidation('alpha', this.validateAlpha);
        this.registerValidation('required', this.required);
        this.registerValidation('email', this.validateEmail);
        this.registerValidation('min', this.validateMin);
        this.registerValidation('max', this.validateMax);
        this.registerValidation('validateTaxNumber', this.validateTaxNumber);
        this.registerValidation('password', this.validatePassword);
        this.registerValidation('date', this.dateValidationFormat);
        this.registerValidation('birthDayValidationFormat', this.birthDayValidationFormat);
        this.registerValidation('pattern', this.pattern);
        this.registerValidation('requiredResponse', this.requiredResponse);
    }

    public requiredResponse = (msg: string, params: string) => {
        return mixed().test('requiredResponse', msg, (response: string) => params === response);
    };

    public pattern = (msg: string, pattern: string) => {
        return string().matches(new RegExp(pattern), msg);
    };

    public registerValidation = (name: string, fn: Function) => {
        this.validators[name] = fn;
    };

    public required = (msg: string) => {
        return string().required(msg);
    };

    public validateNumber = (msg: string) => {
        return string().matches(/^\d+$/, msg);
    };

    public validateAlpha = (msg: string) => {
        return string().matches(/^([^0-9<>%$!~`@#^&*()_\\|+={}';":\/?.,><\[\]])+$/i, msg);
    };

    public validateEmail = (msg: string) => {
        return string().email(msg);
    };

    public validateMin = (msg: string, params: number) => {
        return string().min(params, msg);
    };

    public validateMax = (msg: string, params: number) => {
        return string().max(params, msg);
    };

    public validateTaxNumber = (msg: string) => {
        return string().matches(/^[a-z]{4}[0-9]{6}[0-9a-z]{3}$/i, msg);
    };

    public validatePassword = (msg: string) => {
        return mixed().test('password', msg, (password: string) => {
            if (!password) {
                return false;
            }
            const isLengthValid = password.length >= 8;

            const containsLowerCharacter = password !== password.toUpperCase();

            const containsUpperCharacter = password !== password.toLowerCase();

            const containsNumberOrSpecialCharacter =
                /\d/.test(password) || /[\s!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~\/]+/.test(password);

            return (
                isLengthValid &&
                containsLowerCharacter &&
                containsUpperCharacter &&
                containsNumberOrSpecialCharacter
            );
        });
    };

    public dateValidationFormat = (msg: string) => {
        return mixed().test('date', msg, (date: string) => {
            if (!date) {
                // allow empty fields
                return true;
            }
            // we first check for iso date format (automatically handled by browser)
            // otherwise we check for MM/DD/YYYY required date format
            if (/^(\d{4})-0?(\d+)-0?(\d+)$/.test(date)) {
                return isValid(new Date(date));
            }

            // First check for the pattern
            if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(date)) {
                // format date back to ISO YYYY-MM-DD so we can safely parse
                // months/days validations (including leap years)
                const reformatted = date.replace(
                    /(\d{1,2})\/(\d{1,2})\/(\d{4})/g,
                    (match, month, day, year) => {
                        const monthDate: string = month.length === 1 ? `0${month}` : month;

                        return `${year}-${monthDate}-${day}`;
                    }
                );

                return isValid(new Date(reformatted));
            }

            // there are still some issues with related to cross browser concerns
            return false;
        });
    };

    public birthDayValidationFormat = (msg: string) => {
        return mixed().test('date', msg, (date: string) => {
            if (!date) {
                // allow empty fields
                return true;
            }

            const chars = Array.from(date);

            if (chars.length !== 5) {
                return false;
            }

            const month = Number(`${chars[0]}${chars[1]}`);
            const day = Number(`${chars[3]}${chars[4]}`);

            //only numbers inserted and different from 0
            if (!month || !day) {
                return false;
            }

            // month should be between 1 and 12
            if (month < 1 || month > 12) {
                return false;
            }

            //day should be between 1 and 31
            if (day < 1 || day > 31) {
                return false;
            }

            //if month is february, max day is 29
            if (month === 2 && day > 29) {
                return false;
            }

            // check for 31 days
            const fullMonths = [1, 3, 5, 7, 8, 10, 12];
            if (!fullMonths.filter(fullMonth => fullMonth === month).length) {
                if (day > 30) {
                    return false;
                }
            }

            return true;
        });
    };

    public getValidationSchema = (fieldsObj: IValidatorObj) => {
        const validationSchema = Object.keys(fieldsObj).reduce((validations, fieldName) => {
            const fieldValidations = fieldsObj[fieldName];
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const getValidators: any = () =>
                Object.keys(fieldValidations)
                    .filter(key => this.validators[key.split(':')[0]])
                    .map(validationKey => {
                        const splitIndex = validationKey.indexOf(':');
                        const name =
                            splitIndex === -1 ? validationKey : validationKey.slice(0, splitIndex);
                        const params = splitIndex === -1 ? [] : validationKey.slice(splitIndex + 1);
                        const msg = fieldValidations[validationKey];
                        const validator = this.validators[name];

                        return validator(msg, params);
                    });

            return { ...validations, [fieldName]: compose(getValidators()) };
        }, {});

        return object().shape(validationSchema);
    };
}

export interface IValidator {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    tests: Record<string, any>[];
}
